/* ── The walkthrough canvas ────────────────────────────────────────────
   Turns a room + a project into the engine URL that renders the actual
   tour inside it. This is the thing a guided room is FOR; everything
   else on those two screens is arrangement around it.

   ── THE ENGINE ALREADY DOES THE HARD PART ──
   opensky-vr-engine has driven guided sessions for a while. Its contract
   is three query params (src/app.js, the querystring block):

     role=guide    → the host drives; the engine broadcasts their camera
     role=viewer   → a buyer follows; the engine applies what it receives
     room=<id>     → the sync channel both sides join
     autostart=1   → skip the engine's own start gate, which a buyer who
                     is already in a tour room should never see

   Sync rides wss://vrsync… — the same relay the raised hand uses, and
   the same reason the canvas doesn't have to wait for ws_token: that
   relay authenticates nobody today. The token is the hardening step, not
   what makes the picture move. See utils/guidedRoomRelay.ts.

   ── WHY THE URL IS DELIBERATELY MISSING THINGS ──
   ProjectDetailsV3 builds a fuller version of this URL for its hero
   iframe, carrying agency ids, project slugs and the four person-identity
   params. This one carries LESS on purpose.

   The engine opens a vr_session row whenever its launch context is
   complete, which is (engine/analytics.js resolveVrSessionStartDescriptor):

     agencyId + projectId + (metaModelId|matterportId) + userId   → logs
     agencySlug + projectSlug + experienceSlug                    → logs
     anything short of either                                     → skips

   A guided room's participants are ALREADY recorded server-side by
   verify_otp and guest_join — the guided room API says so in as many
   words: do not start a vr_session for room participants, it duplicates.
   Five people on one tour would otherwise post five sessions on top of
   the five the backend already has, and every tour would inflate the
   analytics the agency actually reads.

   So we pass what the engine needs to LOAD and SYNC — projectId, the
   meta model, the experience slug — and omit what would complete a
   logging context: no agencyId, no agency_slug, no project_slug, no
   user/agent/customer ids. Both paths above stay incomplete, the engine
   logs "skipping session start" to its console, and the room's numbers
   stay the backend's.

   If a param is ever added on the engine side to suppress logging
   explicitly, prefer it to this arrangement — this works by omission,
   and omission is easy to undo by accident. */

export type CanvasSource = {
  /** Where the engine for this experience lives. */
  url: string;
  id: string;
  slug: string;
  name: string;
};

/* Meta models arrive from getPublicProject under several field names
   depending on how the project was authored. Rather than guess one, read
   the ones seen in the wild and drop anything with no URL — an entry we
   can't launch is not an experience, and offering it produces a blank
   iframe with no explanation. */
export const experienceSources = (project: any): CanvasSource[] => {
  const raw = Array.isArray(project?.meta_models) ? project.meta_models : [];
  return raw
    .map((m: any) => ({
      url: String(m?.url || m?.embed_url || m?.experience_url || "").trim(),
      id: String(m?._id || m?.id || m?.meta_model_id || "").trim(),
      slug: String(m?.slug || m?.experience_slug || "").trim(),
      name: String(m?.name || m?.title || "").trim(),
    }))
    .filter((m: CanvasSource) => !!m.url);
};

/* Authors enter these by hand and often leave the scheme off. */
export const canvasBase = (raw: string): string => {
  const s = String(raw || "").trim();
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

export type CanvasRole = "guide" | "viewer";

/* The full URL, or "" when this project has nothing to render — in which
   case the pages keep their placeholder card, which explains itself.

   `roomId` is required rather than optional: a canvas with no room is
   just the ordinary viewer, and rendering that inside a guided tour
   would give everyone their own private walkthrough while the host
   talked about a different room entirely. Worse than showing nothing. */
export const canvasUrl = (
  source: CanvasSource | null | undefined,
  opts: { projectId: string; roomId: string; role: CanvasRole },
): string => {
  const base = canvasBase(source?.url || "");
  const { projectId, roomId, role } = opts;
  if (!base || !roomId) return "";

  const u = new URL(base);
  const set = (k: string, v: string) => { if (v) u.searchParams.set(k, v); };

  set("projectId", String(projectId || ""));
  set("metaModelId", source?.id || "");
  set("experience_slug", source?.slug || "");
  /* 'm' for meta model — the engine's own vocabulary. */
  if (source?.slug) set("experience_type", "m");

  /* ── THE PARAM THE ENGINE ACTUALLY READS ──
     `experience_slug` is OUR name for this; the engine has never read it.
     It reads `exp`, checks it against experiences/manifest.json, and
     ignores anything not in there (app.js, the querystring block). Send
     only `experience_slug` and the engine silently opens whatever sits at
     order: 0 — so a host who set up a room for the Clubhouse got the
     Skywalk, and never had a way to tell.

     Verified against the live Aigen build on 2026-09-07: the console said
     `[VIEWER] Analytics initialized for: skywalk` while the URL asked for
     `aigen-wt`.

     Sending both is safe in the direction that matters. A slug that IS an
     engine experience id now opens the right one; a slug that isn't — an
     authoring mistake, like Aigen's `aigen-wt`, which names no experience
     in that build's manifest — is dropped by the engine and we land on
     the same default we already had. Nothing gets worse, some cases get
     right, and the mismatch becomes visible in the URL rather than
     invisible in the engine. */
  set("exp", source?.slug || "");

  set("room", String(roomId));
  set("role", role);
  u.searchParams.set("autostart", "1");

  /* Not read by the engine today. It costs one query param and it means
     anyone reading a server log, or the engine's console, can see this
     load came from a guided room rather than a normal share. */
  set("guided_room", String(roomId));

  return u.toString();
};

/* Which experience a room shows. There is no picker in the room — the
   host chose the project when they created it, and a control that let
   either side switch mid-tour would desynchronise the two screens for
   everyone. First model wins; if a room ever needs to change experience,
   that's a host action broadcast over the relay, not a local one. */
export const primarySource = (project: any): CanvasSource | null =>
  experienceSources(project)[0] || null;
